import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextField,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Switch,
  TableFooter,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { AppDispatch, RootState } from "../redux/store";
import {
  FaqsItem,
  HelpCenterKeys,
  SocialMediaItem,
} from "../interfaces/help-center-interface";
import {
  getHelpCenterData,
  updateHelpCenter,
} from "../redux/slices/helpCenterSlice";
import useDetectSession from "../hooks/useDetectSession.hook";
import {
  inputStyle,
  pageHeading,
  primaryBtnStyle,
  primaryBtnStyleOutline,
} from "../styles/styling";
import AppContainer from "../components/AppContainer";
import { PermissionsByDocKey } from "../interfaces/permissions-interface";
import { transformArrToObj } from "../utils/transform-permissions-obj";
import { useNavigate } from "react-router-dom";
import LoadingOverlay from "../components/LoadingOverlay";

function insertInStr(str: string, index: number, value: string) {
  return str.substring(0, index) + value + str.substring(index);
}

const HelpCenterPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  useDetectSession();
  const navigate = useNavigate();

  const { helpCenters, status, error } = useSelector(
    (state: RootState) => state.helpCenter
  );

  const currentUser = useSelector((state: RootState) => state.auth.currentUser);

  const [currentUserPermissions, setCurrentUserPermissions] =
    useState<PermissionsByDocKey>();
  const [hideUserActions, setHideUserActions] = useState<boolean>(false);

  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const [editCxLink, setEditCxLink] = useState<boolean>(false);
  const [cxLink, setCxLink] = useState<string>(helpCenters.cxEmail);

  const [editWebsite, setEditWebsite] = useState<boolean>(false);
  const [websiteLink, setWebsiteLink] = useState<string>(helpCenters.website);

  const [editSocialMediaLink, setEditSocialMediaLink] = useState<
    undefined | number
  >(undefined);
  const [socialMediaLinks, setSocialMediaLinks] = useState<SocialMediaItem[]>(
    helpCenters.socialMedia
  );

  const [editFaqs, setEditFaqs] = useState<undefined | number>(undefined);
  const [updatedFaqs, setUpdatedFaqs] = useState<FaqsItem[]>(helpCenters.faqs);
  const [removeFaqPrompt, setRemoveFaqPrompt] = useState<boolean>(false);
  const [indexToRemove, setIndexToRemove] = useState<number | undefined>(
    undefined
  );
  const [addBullet, setAddBullet] = useState<boolean>(false);
  const ansRef = useRef<any>();
  const [selectionStart, setSelectionStart] = useState();
  const updateSelectionStart = () => {
    setSelectionStart(ansRef?.current?.selectionStart);
  };

  useEffect(() => {
    dispatch(getHelpCenterData());
  }, [dispatch]);

  useEffect(() => {
    if (!currentUser) {
      navigate("/login");
    }
    if (currentUser && currentUser.userPermissions) {
      setCurrentUserPermissions(transformArrToObj(currentUser.userPermissions));
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (currentUserPermissions) {
      setHideUserActions(
        currentUserPermissions?.helpCenter?.permissions?.edit !== true
      );
    }
  }, [currentUserPermissions]);

  useEffect(() => {
    if (!editCxLink) {
      setCxLink(helpCenters.cxEmail);
    }
    if (!editWebsite) {
      setWebsiteLink(helpCenters.website);
    }
    if (editSocialMediaLink === undefined) {
      setSocialMediaLinks(helpCenters.socialMedia);
    }
    if (editFaqs === undefined) {
      setUpdatedFaqs(helpCenters.faqs);
    }
  }, [editCxLink, editFaqs, editSocialMediaLink, editWebsite, helpCenters]);

  const handleSaveClick = ({
    helpCenterKey,
    value,
  }: {
    helpCenterKey: HelpCenterKeys;
    value: string | FaqsItem[] | SocialMediaItem[];
  }) => {
    const obj = {
      [helpCenterKey]: value,
      updatedBy: currentUser?.fullname ?? "",
    };

    dispatch(updateHelpCenter(obj))
      .unwrap()
      .then(() => {
        setSnackbarMessage("Help center updated successfully!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
        dispatch(getHelpCenterData());
      })
      .catch((err: any) => {
        setSnackbarMessage(`Failed to update help center: ${err}`);
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      });
  };

  const renderSocialMediaItem = (item: SocialMediaItem, index: number) => {
    return editSocialMediaLink !== index ? (
      <TableRow key={item.name}>
        <TableCell>{item.name}</TableCell>
        <TableCell>{item.link}</TableCell>
        <TableCell>{item.isHidden ? "Yes" : "No"}</TableCell>
        {!hideUserActions && (
          <TableCell>
            <IconButton
              onClick={() => {
                setEditSocialMediaLink(index);
              }}
            >
              <EditIcon />
            </IconButton>
          </TableCell>
        )}
      </TableRow>
    ) : (
      <TableRow key={item.name}>
        <TableCell>
          <TextField
            value={socialMediaLinks[editSocialMediaLink]?.name}
            onChange={(e) => {
              const newVal = {
                name: e.target.value,
                link: socialMediaLinks[index].link,
                isHidden: socialMediaLinks[index].isHidden,
              };
              setSocialMediaLinks(
                socialMediaLinks.map((newItem, currentIndex) => {
                  if (currentIndex === index) {
                    return newVal;
                  }
                  return newItem;
                })
              );
            }}
            fullWidth
            placeholder="Name"
            sx={inputStyle()}
          />
        </TableCell>
        <TableCell>
          <TextField
            value={socialMediaLinks[editSocialMediaLink]?.link}
            onChange={(e) => {
              const newVal = {
                name: socialMediaLinks[index].name,
                link: e.target.value,
                isHidden: socialMediaLinks[index].isHidden,
              };
              setSocialMediaLinks(
                socialMediaLinks.map((newItem, currentIndex) => {
                  if (currentIndex === index) {
                    return newVal;
                  }
                  return newItem;
                })
              );
            }}
            fullWidth
            placeholder="Link"
            sx={inputStyle()}
          />
        </TableCell>
        <TableCell>
          <Switch
            checked={socialMediaLinks[editSocialMediaLink]?.isHidden || false}
            onChange={() => {
              const newVal = {
                name: socialMediaLinks[index].name,
                link: socialMediaLinks[index].link,
                isHidden: !socialMediaLinks[index].isHidden,
              };
              setSocialMediaLinks(
                socialMediaLinks.map((newItem, currentIndex) => {
                  if (currentIndex === index) {
                    return newVal;
                  }
                  return newItem;
                })
              );
            }}
          />
        </TableCell>
        <TableCell>
          <Box flexDirection={"row"}>
            <Button
              variant="contained"
              onClick={() => {
                handleSaveClick({
                  helpCenterKey: HelpCenterKeys.socialMedia,
                  value: socialMediaLinks,
                });
                setEditSocialMediaLink(undefined);
              }}
              style={{ marginRight: 15 }}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setEditSocialMediaLink(undefined);
              }}
            >
              Cancel
            </Button>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const renderFaqsItem = (item: FaqsItem, index: number) => {
    return editFaqs !== index ? (
      <TableRow key={index}>
        <TableCell>{item.question}</TableCell>
        <TableCell>{item.answer}</TableCell>
        {!hideUserActions && (
          <TableCell>
            <IconButton
              onClick={() => {
                setEditFaqs(index);
              }}
            >
              <EditIcon />
            </IconButton>
          </TableCell>
        )}
        {!hideUserActions && (
          <TableCell>
            <IconButton
              onClick={() => {
                setRemoveFaqPrompt(true);
                setIndexToRemove(index);
              }}
            >
              <RemoveCircleOutlineIcon />
            </IconButton>
          </TableCell>
        )}
      </TableRow>
    ) : (
      <TableRow key={index}>
        <TableCell>
          <TextField
            value={updatedFaqs[editFaqs]?.question}
            onChange={(e) => {
              const newVal = {
                question: e.target.value,
                answer: updatedFaqs[index].answer,
              };
              setUpdatedFaqs(
                updatedFaqs.map((newItem, currentIndex) => {
                  if (currentIndex === index) {
                    return newVal;
                  }
                  return newItem;
                })
              );
            }}
            fullWidth
            placeholder="Question"
            sx={inputStyle()}
          />
        </TableCell>
        <TableCell>
          <Tooltip title="Insert Bullet">
            <IconButton
              color={addBullet ? "primary" : "default"}
              onClick={() => {
                setAddBullet(!addBullet);
              }}
            >
              <FormatListBulletedIcon />
            </IconButton>
          </Tooltip>
          <TextField
            value={updatedFaqs[editFaqs]?.answer}
            multiline
            inputRef={ansRef}
            onSelect={() => updateSelectionStart()}
            onChange={(e) => {
              const newVal = {
                answer: e.target.value,
                question: updatedFaqs[index].question,
              };
              setUpdatedFaqs(
                updatedFaqs.map((newItem, currentIndex) => {
                  if (currentIndex === index) {
                    return newVal;
                  }
                  return newItem;
                })
              );
            }}
            onKeyDown={(e) => {
              let newAns = updatedFaqs[index].answer;
              if (addBullet && e.key === "Enter") {
                newAns = insertInStr(
                  newAns,
                  selectionStart ?? newAns.length - 1,
                  "•"
                );
                const newVal = {
                  answer: newAns,
                  question: updatedFaqs[index].question,
                };
                setUpdatedFaqs(
                  updatedFaqs.map((newItem, currentIndex) => {
                    if (currentIndex === index) {
                      return newVal;
                    }
                    return newItem;
                  })
                );
              }
            }}
            fullWidth
            placeholder="Answer"
            sx={[inputStyle(), { height: "auto" }]}
          />
        </TableCell>
        <TableCell width={180}>
          <Box flexDirection={"row"}>
            <Button
              variant="contained"
              onClick={() => {
                handleSaveClick({
                  helpCenterKey: HelpCenterKeys.faqs,
                  value: updatedFaqs,
                });
                setEditFaqs(undefined);
              }}
              style={{ marginRight: 15 }}
              sx={primaryBtnStyle}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setEditFaqs(undefined);
                setAddBullet(false);
              }}
              sx={primaryBtnStyleOutline}
            >
              Cancel
            </Button>
          </Box>
        </TableCell>
        <TableCell>
          <IconButton
            onClick={() => {
              if (!item.answer && !item.question && index !== editFaqs) {
                setIndexToRemove(index);
                setRemoveFaqPrompt(true);
              } else {
                setUpdatedFaqs(
                  updatedFaqs.filter((faq, faqIndex) => faqIndex !== index)
                );
              }
            }}
          >
            <RemoveCircleOutlineIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  if (status === "failed") {
    return (
      <AppContainer>
        <Typography variant="h6" color="error">
          Error: {error}
        </Typography>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <LoadingOverlay loading={status === "loading"} />
      <Box display="flex" alignItems="center" marginBottom={2}>
        <Typography variant="h4" sx={pageHeading}>
          Help Center
        </Typography>
      </Box>

      {/* CX Center Email */}
      <Box
        display="flex"
        alignItems="center"
        margin={2}
        flexDirection={"column"}
      >
        <Typography variant="h6">CX Center</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>CX Email</TableCell>
                {!hideUserActions && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  {!editCxLink ? (
                    helpCenters.cxEmail
                  ) : (
                    <TextField
                      label="CX Email"
                      value={cxLink}
                      onChange={(e) => setCxLink(e.target?.value)}
                      fullWidth
                      margin="dense"
                      sx={inputStyle()}
                    />
                  )}
                </TableCell>
                {!hideUserActions && (
                  <TableCell>
                    {!editCxLink ? (
                      <IconButton
                        onClick={() => {
                          setEditCxLink(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    ) : (
                      <Box flexDirection={"row"}>
                        <Button
                          variant="contained"
                          onClick={() => {
                            handleSaveClick({
                              helpCenterKey: HelpCenterKeys.cxEmail,
                              value: cxLink,
                            });
                            setEditCxLink(false);
                          }}
                          style={{ marginRight: 15 }}
                          sx={primaryBtnStyle}
                        >
                          Save
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setEditCxLink(false);
                            setCxLink(helpCenters.cxEmail);
                          }}
                          sx={primaryBtnStyleOutline}
                        >
                          Cancel
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                )}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Malayan Website */}
      <Box
        display="flex"
        alignItems="center"
        margin={2}
        flexDirection={"column"}
      >
        <Typography variant="h6">Website</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Website</TableCell>
                {!hideUserActions && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  {!editWebsite ? (
                    helpCenters.website
                  ) : (
                    <TextField
                      label="Website"
                      value={websiteLink}
                      onChange={(e) => setWebsiteLink(e.target?.value)}
                      fullWidth
                      margin="dense"
                      sx={inputStyle()}
                    />
                  )}
                </TableCell>
                {!hideUserActions && (
                  <TableCell>
                    {!editWebsite ? (
                      <IconButton
                        onClick={() => {
                          setEditWebsite(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    ) : (
                      <Box flexDirection={"row"}>
                        <Button
                          variant="contained"
                          style={{ marginRight: 15 }}
                          onClick={() => {
                            handleSaveClick({
                              helpCenterKey: HelpCenterKeys.website,
                              value: websiteLink,
                            });
                            setEditWebsite(false);
                          }}
                          sx={primaryBtnStyle}
                        >
                          Save
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setEditWebsite(false);
                            setWebsiteLink(helpCenters.website);
                          }}
                          sx={primaryBtnStyleOutline}
                        >
                          Cancel
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                )}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Social Media Links */}
      <Box
        display="flex"
        alignItems="center"
        margin={2}
        flexDirection={"column"}
      >
        <Typography variant="h6">Social Media</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Link</TableCell>
                <TableCell>Hidden</TableCell>
                {!hideUserActions && <TableCell>Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {helpCenters?.socialMedia?.map((item: SocialMediaItem, index) =>
                renderSocialMediaItem(item, index)
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* FAQs */}
      <Box
        display="flex"
        alignItems="center"
        margin={2}
        flexDirection={"column"}
      >
        <Typography variant="h6">FAQs</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Question</TableCell>
                <TableCell>Answer</TableCell>
                {!hideUserActions && <TableCell>Update</TableCell>}
                {!hideUserActions && <TableCell>Remove</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {updatedFaqs?.map((item: FaqsItem, index) =>
                renderFaqsItem(item, index)
              )}
            </TableBody>
            {!hideUserActions && (
              <TableFooter>
                <TableRow>
                  <TableCell>
                    <IconButton
                      onClick={() => {
                        setUpdatedFaqs([
                          ...updatedFaqs,
                          { question: "", answer: "" },
                        ]);
                        setEditFaqs(updatedFaqs.length);
                      }}
                    >
                      <AddCircleOutlineIcon />
                      <Typography paddingLeft={5} variant="h6">
                        Add Another
                      </Typography>
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Box>

      <Dialog
        open={removeFaqPrompt}
        onClose={() => {
          setIndexToRemove(undefined);
          setRemoveFaqPrompt(false);
        }}
        aria-labelledby="Remove FAQ"
      >
        <DialogTitle id="alert-dialog-title">Delete FAQ</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this FAQ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIndexToRemove(undefined);
              setRemoveFaqPrompt(false);
            }}
            color="primary"
            sx={[primaryBtnStyleOutline, { width: "60px" }]}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!indexToRemove) {
                return;
              }

              const faqUpdatePayload = updatedFaqs.filter(
                (faq, faqIndex) => faqIndex !== indexToRemove
              );

              handleSaveClick({
                helpCenterKey: HelpCenterKeys.faqs,
                value: faqUpdatePayload,
              });

              setUpdatedFaqs(faqUpdatePayload);
              setRemoveFaqPrompt(false);
            }}
            autoFocus
            sx={[primaryBtnStyleOutline, { width: "75px" }]}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </AppContainer>
  );
};

export default HelpCenterPage;
